import * as React from "react";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import DoubleEliminationBracket from "bracket-system/bracket-double/double-elim-bracket";
import { EventStageTypes } from "common/constants/EventStageTypes";
import Match from "bracket-system/components/match";
import { getBracketOptions, parsePicksIntoBracketMatches } from "common/helpers/bracketHelper";
import {
    // eslint-disable-next-line no-unused-vars
    EventFormatData,
    getHeaderNamesFromBracket,
    getLockedColumns,
} from "common/helpers/eventFormatHelper";
import BracketEditor from "./BracketEditor";
// eslint-disable-next-line no-unused-vars
import { EventStage } from "common/types/EventStage";
import FreeformBracket from "bracket-system/bracket-freeform/freeform-bracket";
import { useBracketSurface } from "./BracketSurface";

const propKeys = {
    eventData: StoreKeys.EVENT.EVENTDATA,
    format: StoreKeys.EVENT.FORMAT,
    userBracketPicks: StoreKeys.EVENT.USER.BRACKET_PICKS,
    userGroupPicks: StoreKeys.EVENT.USER.GROUP_PICKS,
    officialBracketPicks: StoreKeys.EVENT.OFFICIAL.BRACKET_PICKS,
    officialGroupPicks: StoreKeys.EVENT.OFFICIAL.GROUP_PICKS,
    isEditing: StoreKeys.EVENT.IS_EDITING,
    stageIndex: StoreKeys.EVENT.STAGE_INDEX,
    substageIndex: StoreKeys.EVENT.SUBSTAGE_INDEX,
    showOfficial: StoreKeys.EVENT.SHOW_OFFICIAL,
};
/**
 * @typedef BracketRendererCProps
 * @type {object}
 * @property {any} eventData
 * @property {EventFormatData} format
 * @property {MatchPick[]} userBracketPicks
 * @property {MatchPick[]} officialBracketPicks
 * @property {boolean} isEditing
 * @property {number} stageIndex
 * @property {number} substageIndex
 * @property {boolean} showOfficial
 */

/**
 * @param {BracketRendererCProps} props
 */
function BracketRendererC({
    eventData,
    format,
    userBracketPicks,
    officialBracketPicks,
    isEditing,
    stageIndex,
    substageIndex,
    showOfficial,
}) {
    const { ready, svgWrapper, surfaceProps } = useBracketSurface();

    const currentStage = format.stages[stageIndex];
    /** @type {EventStage} */
    const currentEventStage = eventData.eventStages[stageIndex];
    const currentSubstage = currentEventStage.eventSubstage[substageIndex];
    const headerNames = getHeaderNamesFromBracket(currentStage.bracket);
    const lockedColumns = getLockedColumns(currentStage.bracket);
    const useSeriesScore =
        currentStage?.type === EventStageTypes.Playoffs
            ? currentStage?.playoffDetails?.useSeriesScore
            : currentStage?.groupStageDetails?.useSeriesScore;

    const officialMatches = parsePicksIntoBracketMatches(
        eventData,
        currentEventStage,
        currentSubstage,
        currentStage,
        currentStage.bracket,
        [],
        officialBracketPicks
    );

    const matches = parsePicksIntoBracketMatches(
        eventData,
        currentEventStage,
        currentSubstage,
        currentStage,
        currentStage.bracket,
        userBracketPicks ?? [],
        officialBracketPicks
    );

    return (
        <>
            {isEditing && <BracketEditor />}
            {!isEditing && (
                <>
                    {currentStage.bracket.freeform && (
                        <div {...surfaceProps}>
                            {ready && (
                                <FreeformBracket
                                    parts={currentStage.bracket.freeformParts}
                                    matches={
                                        showOfficial ? officialMatches : matches
                                    }
                                    officialMatches={
                                        showOfficial ? [] : officialMatches
                                    }
                                    matchComponent={Match}
                                    options={getBracketOptions()}
                                    svgWrapper={svgWrapper}
                                    hideSeriesScore={!useSeriesScore}
                                />
                            )}
                        </div>
                    )}
                    {!currentStage.bracket.freeform && (
                        <div {...surfaceProps}>
                            {ready && (
                                <DoubleEliminationBracket
                                    matches={
                                        showOfficial ? officialMatches : matches
                                    }
                                    officialMatches={
                                        showOfficial ? [] : officialMatches
                                    }
                                    matchComponent={Match}
                                    options={getBracketOptions()}
                                    svgWrapper={svgWrapper}
                                    customHeaderNames={headerNames}
                                    lockedColumns={lockedColumns}
                                    hideSeriesScore={!useSeriesScore}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default function BracketRenderer(props) {
    return connect(<BracketRendererC />, propKeys, props);
}
